import {
  Component,
  HostListener,
  Input,
  forwardRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { NgpToggleGroup, NgpToggleGroupItem } from 'ng-primitives/toggle-group';

import {
  SuiFormFieldControl,
  SUI_FORM_FIELD_CONTROL,
} from '@sensika/sui/form-field';
import { SuiFormControlBase } from '@sensika/sui/shared';

export type SuiToggleVariant = 'filter' | 'nullable';
export type SuiToggleValue = number | boolean | null;

interface SuiToggleOption {
  /**
   * Primitive item value + `[data-state]` colour key. NgpToggleGroup is typed
   * for `string` values, so this string is what crosses the primitive boundary;
   * `value` is the typed value we expose through the CVA.
   */
  key: string;
  /** The public value bound/emitted for this position (1:1 with legacy app-toggle). */
  value: SuiToggleValue;
  /** Accessible label for the position. */
  label: string;
}

// Left → middle → right. Values mirror the legacy `app-toggle` exactly so the
// eventual migration is 1:1 (filter: getItemCheckType 0/1/2; nullable: false/null/true).
const TOGGLE_OPTIONS: Record<SuiToggleVariant, SuiToggleOption[]> = {
  filter: [
    { key: 'exclude', value: 0, label: 'Exclude' },
    { key: 'neutral', value: 1, label: 'Neutral' },
    { key: 'include', value: 2, label: 'Include' },
  ],
  nullable: [
    { key: 'off', value: false, label: 'Off' },
    { key: 'null', value: null, label: 'Mixed' },
    { key: 'on', value: true, label: 'On' },
  ],
};

const POSITIONS = ['left', 'middle', 'right'] as const;

@Component({
  selector: 'sui-toggle',
  standalone: true,
  imports: [NgpToggleGroup, NgpToggleGroupItem],
  templateUrl: './sui-toggle.component.html',
  styleUrl: './sui-toggle.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiToggleComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiToggleComponent),
    },
  ],
})
export class SuiToggleComponent
  extends SuiFormControlBase<SuiToggleValue>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-toggle-${++SuiToggleComponent._idCounter}`;
  private _focused = false;

  readonly controlType = 'toggle';

  @Input() variant: SuiToggleVariant = 'filter';
  @Input() label = '';
  @Input() id = '';

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }

  get focused(): boolean {
    return this._focused;
  }

  get empty(): boolean {
    return this.value() == null;
  }

  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  get options(): SuiToggleOption[] {
    return TOGGLE_OPTIONS[this.variant];
  }

  // The option to render. Falls back to the middle position (filter → neutral,
  // nullable → null) when the bound value is unset / out of domain, so the thumb
  // always has a stop.
  get displayOption(): SuiToggleOption {
    const v = this.value();
    return this.options.find((o) => o.value === v) ?? this.options[1];
  }

  /** Wrapped string-key for NgpToggleGroup's array value input. */
  get groupValue(): string[] {
    return [this.displayOption.key];
  }

  get selectedState(): string {
    return this.displayOption.key;
  }

  get selectedPosition(): (typeof POSITIONS)[number] {
    return POSITIONS[this.options.indexOf(this.displayOption)] ?? 'middle';
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
    this.markTouched();
  }

  onValueChange(keys: string[]): void {
    const option = this.options.find((o) => o.key === keys[0]);
    if (option) {
      this.setValue(option.value);
    }
  }
}
